import { CustomerChangesTab } from './customer-changes-tab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Download, Pencil, Plus, Receipt, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BILLING_STATUS, BillingBadge, BillingLines, type BillingDetail, type BillingRecord, type BillingStatus, type FlatRate } from '@/components/billing';
import { centsToInput, formatCents, parseEuro } from '@/components/repairs';
import { Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Paginated } from '@/lib/types';
import { formatDate, formatDateTime } from '@/lib/utils';

const onError = (e: unknown) => toast.error(errorMessage(e));
const hours = (m: number) => `${(m / 60).toFixed(2).replace('.', ',')} Std.`;

/** Detailansicht eines Abrechnungsvorgangs mit Übernahme, „Keine Rechnung“ und Rückfrage. */
function RecordDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canManage = can('billing.manage');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const { data: r } = useQuery({ queryKey: ['billing', 'detail', id], queryFn: () => api<BillingDetail>(`/billing/${id}`), enabled: id !== null });
  useEffect(() => {
    setReference('');
    setNote('');
  }, [id]);
  const done = (msg: string) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ['billing'] });
    onClose();
  };
  const decide = useMutation({
    mutationFn: (d: 'approve' | 'reject') => api(`/billing/${id}/change-request/${d}`, { method: 'POST', body: { note: note || null } }),
    onSuccess: (_, d) => done(d === 'approve' ? 'Änderung genehmigt – der Vorgang ist wieder bearbeitbar' : 'Änderungsantrag abgelehnt'),
    onError,
  });
  const act = useMutation({
    mutationFn: (a: 'transfer' | 'not-billable' | 'reopen' | 'return') =>
      api<{ ticket?: { number: string }; assignee?: string | null }>(`/billing/${id}/${a}`, { method: 'POST', body: a === 'return' ? { note } : { reference: reference || null, note: note || null } }),
    onSuccess: (res, a) =>
      done(
        a === 'return'
          ? `Rückfrage als Ticket ${res.ticket?.number} angelegt${res.assignee ? ` – zugewiesen an ${res.assignee}` : ' (nicht zugewiesen)'}`
          : { transfer: 'Als in Powerbird übernommen markiert', 'not-billable': 'Als „Keine Rechnung“ abgeschlossen', reopen: 'Wieder bereit zur Rechnung' }[a],
      ),
    onError,
  });
  return (
    <Dialog open={id !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>
            {r ? `${r.type === 'ticket' ? 'Ticket' : 'Reparatur'} ${r.ref.number}` : 'Abrechnungsvorgang'}
          </DialogTitle>
          <DialogDescription>{r ? `${r.customer?.name ?? 'ohne Kunde'}${r.customer ? ` · Kundennummer ${r.customer.number}` : ''} · ${r.ref.title}` : ''}</DialogDescription>
        </DialogHeader>
        {!r ? (
          <DialogBody>
            <Skeleton className="h-60" />
          </DialogBody>
        ) : (
          <DialogBody className="grid gap-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <BillingBadge status={r.status} />
              <span className="text-muted-foreground">
                freigegeben {formatDateTime(r.releasedAt)}
                {r.releasedByName ? ` von ${r.releasedByName}` : ''} · Vorgang {r.ref.state}
              </span>
              <Link to={r.type === 'ticket' ? `/tickets/${r.ref.id}` : `/repairs/${r.ref.id}`} className="ml-auto text-primary hover:underline">
                {r.type === 'ticket' ? 'Ticket' : 'Reparatur'} öffnen
              </Link>
            </div>
            {r.note && <Alert>Hinweis der Technik: {r.note}</Alert>}
            {r.changeRequest && (
              <Alert variant="destructive">
                <strong>Änderungsantrag</strong> von {r.changeRequest.requestedByName ?? 'unbekannt'} ({formatDateTime(r.changeRequest.requestedAt)}): „{r.changeRequest.reason}“
                {r.status === 'TRANSFERRED' && <span className="block">Bereits in Powerbird übernommen – bei Genehmigung den Beleg dort korrigieren.</span>}
                {canManage && (
                  <span className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => decide.mutate('approve')} loading={decide.isPending}>
                      Genehmigen (wieder bearbeitbar)
                    </Button>
                    <Button size="sm" variant="outline" disabled={!note.trim()} onClick={() => decide.mutate('reject')}>
                      Ablehnen (Grund unter „Notiz“)
                    </Button>
                  </span>
                )}
              </Alert>
            )}
            <BillingLines summary={r.summary} />
            {r.status !== 'READY' && (
              <p className="text-sm text-muted-foreground">
                {BILLING_STATUS[r.status].label} am {formatDateTime(r.processedAt)}
                {r.processedByName ? ` von ${r.processedByName}` : ''}
                {r.reference ? ` · Beleg ${r.reference}` : ''}
                {r.processNote ? ` · ${r.processNote}` : ''}
              </p>
            )}
            {canManage && r.status === 'READY' && (
              <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2">
                <Field label="Beleg-/Auftragsnummer in Powerbird" htmlFor="b-ref">
                  <Input id="b-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="optional" />
                </Field>
                <Field label="Notiz / Grund" htmlFor="b-note" hint="Pflicht bei „Keine Rechnung“ und Rückfrage">
                  <Textarea id="b-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </div>
            )}
          </DialogBody>
        )}
        <DialogFooter>
          {canManage && r?.status === 'READY' && (
            <>
              <Button variant="ghost" className="mr-auto" disabled={note.trim().length < 3} onClick={() => act.mutate('return')}>
                Rückfrage an Technik
              </Button>
              <Button variant="outline" disabled={!note.trim()} onClick={() => act.mutate('not-billable')}>
                Keine Rechnung
              </Button>
              <Button onClick={() => act.mutate('transfer')} loading={act.isPending}>
                <CheckCheck /> In Powerbird übernommen
              </Button>
            </>
          )}
          {canManage && r && r.status !== 'READY' && (
            <Button variant="outline" onClick={() => act.mutate('reopen')}>
              Wieder öffnen (Korrektur)
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordsTab({ status }: { status: BillingStatus }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [type, setType] = useState<'all' | 'ticket' | 'repair'>('all');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => {
    const t = setTimeout(() => (setDebounced(q), setPage(1)), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setSelected([]), [status, page, type, debounced]);
  const query = new URLSearchParams({ status, type, page: String(page), pageSize: '50', ...(debounced && { q: debounced }) });
  const { data } = useQuery({ queryKey: ['billing', 'list', query.toString()], queryFn: () => api<Paginated<BillingRecord>>(`/billing?${query}`) });
  const bulk = useMutation({
    mutationFn: () => api<{ count: number }>('/billing/transfer', { method: 'POST', body: { ids: selected } }),
    onSuccess: (r) => {
      toast.success(`${r.count} Vorgänge als übernommen markiert`);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError,
  });
  const exportUrl = `/api/billing/export?${new URLSearchParams({ status, type, ...(debounced && { q: debounced }) })}`;
  const selectable = status === 'READY' && can('billing.manage');
  const all = data?.items.map((i) => i.id) ?? [];
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Nummer, Kunde, Belegnummer" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Suche" />
        </div>
        <Select className="w-auto" value={type} onChange={(e) => (setType(e.target.value as typeof type), setPage(1))} aria-label="Art">
          <option value="all">Tickets und Reparaturen</option>
          <option value="ticket">Nur Tickets</option>
          <option value="repair">Nur Reparaturen</option>
        </Select>
        <div className="ml-auto flex gap-2">
          {selectable && selected.length > 0 && (
            <Button size="sm" onClick={() => bulk.mutate()} loading={bulk.isPending}>
              <CheckCheck /> {selected.length} als übernommen markieren
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <a href={exportUrl}>
              <Download /> CSV
            </a>
          </Button>
        </div>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            {selectable && (
              <TH className="w-8">
                <Checkbox checked={all.length > 0 && selected.length === all.length} onCheckedChange={(v) => setSelected(v ? all : [])} aria-label="Alle auswählen" />
              </TH>
            )}
            <TH>Vorgang</TH>
            <TH>Kunde</TH>
            <TH className="hidden md:table-cell">{status === 'READY' ? 'Freigegeben' : 'Bearbeitet'}</TH>
            <TH className="text-right">Umfang</TH>
            {status !== 'READY' && <TH className="hidden lg:table-cell">Beleg</TH>}
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && (
            <EmptyRow colSpan={6}>
              <span className="inline-flex items-center gap-2">
                <Receipt className="size-4" /> {status === 'READY' ? 'Keine Vorgänge bereit zur Rechnung.' : 'Keine Vorgänge.'}
              </span>
            </EmptyRow>
          )}
          {data?.items.map((r) => (
            <TR key={r.id} className="cursor-pointer" onClick={() => setParams({ id: r.id })}>
              {selectable && (
                <TD onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.includes(r.id)} onCheckedChange={(v) => setSelected((s) => (v ? [...s, r.id] : s.filter((x) => x !== r.id)))} aria-label={`${r.ref.number} auswählen`} />
                </TD>
              )}
              <TD>
                <p className="font-medium">
                  <Badge variant="outline">{r.type === 'ticket' ? 'Ticket' : 'Reparatur'}</Badge> <span className="font-mono">{r.ref.number}</span>
                </p>
                <p className="max-w-80 truncate text-xs text-muted-foreground">
                  {r.ref.title} · {r.ref.state}
                </p>
                {r.note && <p className="max-w-80 truncate text-xs">Hinweis: {r.note}</p>}
                {r.changeRequest && <Badge variant="destructive">Änderungsantrag</Badge>}
              </TD>
              <TD className="text-sm">
                {r.customer ? (
                  <>
                    {r.customer.name}
                    <span className="block font-mono text-xs text-muted-foreground">{r.customer.number}</span>
                  </>
                ) : (
                  '–'
                )}
              </TD>
              <TD className="hidden whitespace-nowrap text-sm md:table-cell">
                {formatDate(status === 'READY' ? r.releasedAt : r.processedAt)}
                <span className="block text-xs text-muted-foreground">{status === 'READY' ? r.releasedByName : r.processedByName}</span>
              </TD>
              <TD className="whitespace-nowrap text-right text-sm tabular-nums">{r.net !== null ? `${formatCents(r.net)} netto` : r.minutes !== null ? hours(r.minutes) : '–'}</TD>
              {status !== 'READY' && <TD className="hidden text-sm lg:table-cell">{r.reference ?? r.processNote ?? '–'}</TD>}
            </TR>
          ))}
        </TBody>
      </Table>
      {data && <Pagination page={page} pageSize={50} total={data.total} onPage={setPage} />}
      <RecordDialog id={params.get('id')} onClose={() => setParams({})} />
    </Card>
  );
}

interface RateForm {
  id: string | null;
  name: string;
  description: string;
  price: string;
  creditOnRepair: boolean;
  autoAdd: boolean;
  active: boolean;
  sortOrder: number;
}
const emptyRate: RateForm = { id: null, name: '', description: '', price: '', creditOnRepair: true, autoAdd: false, active: true, sortOrder: 0 };

function FlatRatesTab() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canManage = can('billing.manage');
  const [edit, setEdit] = useState<RateForm | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const { data } = useQuery({ queryKey: ['repair-flat-rates'], queryFn: () => api<{ items: FlatRate[] }>('/repair-flat-rates') });
  const saved = (v: { items: FlatRate[] }) => {
    qc.setQueryData(['repair-flat-rates'], v);
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: (f: RateForm) => {
      const body = { name: f.name, description: f.description || null, priceCents: parseEuro(f.price) ?? 0, creditOnRepair: f.creditOnRepair, autoAdd: f.autoAdd, active: f.active, sortOrder: f.sortOrder };
      return f.id ? api<{ items: FlatRate[] }>(`/repair-flat-rates/${f.id}`, { method: 'PUT', body }) : api<{ items: FlatRate[] }>('/repair-flat-rates', { method: 'POST', body });
    },
    onSuccess: (v) => (saved(v), toast.success('Gespeichert')),
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<{ items: FlatRate[] }>(`/repair-flat-rates/${id}`, { method: 'DELETE' }), onSuccess: saved, onError });
  const presets = [
    { name: 'Diagnose und Kostenvoranschlag', price: '49,00', creditOnRepair: true, autoAdd: true, description: 'Fehlerdiagnose und Erstellung des Kostenvoranschlags' },
    { name: 'Expresszuschlag', price: '29,00', creditOnRepair: false, autoAdd: false, description: 'Bevorzugte Bearbeitung' },
    { name: 'Datensicherung', price: '39,00', creditOnRepair: false, autoAdd: false, description: 'Sicherung und Rückspielen der Daten' },
  ].filter((p) => !data?.items.some((i) => i.name === p.name));
  return (
    <Card>
      {confirmDialog}
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Reparaturpauschalen</CardTitle>
          <CardDescription>
            Beträge netto. „Wird verrechnet“: Die Pauschale wird gutgeschrieben, wenn die Reparatur durchgeführt wird (z. B. Diagnose/KVA). „Vorausgewählt“: bei der Geräteannahme automatisch gesetzt (nicht bei Garantie/Kulanz).
          </CardDescription>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setEdit(emptyRate)}>
            <Plus /> Pauschale
          </Button>
        )}
      </CardHeader>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Pauschale</TH>
            <TH className="text-right">Betrag netto</TH>
            <TH className="hidden md:table-cell">Regeln</TH>
            <TH className="hidden md:table-cell text-right">Verwendet</TH>
            {canManage && <TH className="w-20" />}
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={5}>Noch keine Pauschalen angelegt.</EmptyRow>}
          {data?.items.map((r) => (
            <TR key={r.id}>
              <TD>
                <p className="font-medium">
                  {r.name} {!r.active && <Badge variant="outline">inaktiv</Badge>}
                </p>
                {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
              </TD>
              <TD className="text-right tabular-nums">{formatCents(r.priceCents)}</TD>
              <TD className="hidden md:table-cell">
                <div className="flex flex-wrap gap-1">
                  {r.creditOnRepair && <Badge variant="success">wird verrechnet</Badge>}
                  {r.autoAdd && <Badge>vorausgewählt</Badge>}
                </div>
              </TD>
              <TD className="hidden text-right text-sm md:table-cell">{r.usage}×</TD>
              {canManage && (
                <TD>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="size-8" aria-label={`${r.name} bearbeiten`} onClick={() => setEdit({ ...r, description: r.description ?? '', price: centsToInput(r.priceCents) })}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      aria-label={`${r.name} löschen`}
                      onClick={async () => (await confirm({ title: `Pauschale „${r.name}“ löschen?`, description: 'Bereits erfasste Pauschalen an Reparaturen bleiben erhalten.', confirmLabel: 'Löschen', destructive: true })) && del.mutate(r.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TD>
              )}
            </TR>
          ))}
        </TBody>
      </Table>
      {canManage && presets.length > 0 && (
        <CardContent className="flex flex-wrap items-center gap-2 border-t pt-4">
          <span className="text-xs text-muted-foreground">Vorlagen:</span>
          {presets.map((p) => (
            <Button key={p.name} size="sm" variant="outline" onClick={() => setEdit({ ...emptyRate, ...p })}>
              <Plus /> {p.name}
            </Button>
          ))}
        </CardContent>
      )}
      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (edit) save.mutate(edit);
            }}
          >
            <DialogHeader>
              <DialogTitle>{edit?.id ? 'Pauschale bearbeiten' : 'Neue Pauschale'}</DialogTitle>
              <DialogDescription>Änderungen gelten für künftige Reparaturen; bereits erfasste Pauschalen bleiben unverändert.</DialogDescription>
            </DialogHeader>
            {edit && (
              <DialogBody className="grid gap-4">
                <Field label="Bezeichnung" htmlFor="fr-name">
                  <Input id="fr-name" required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                </Field>
                <Field label="Beschreibung" htmlFor="fr-desc">
                  <Input id="fr-desc" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                </Field>
                <Field label="Betrag (netto, €)" htmlFor="fr-price" error={edit.price && parseEuro(edit.price) === null ? 'Ungültiger Betrag' : undefined}>
                  <Input id="fr-price" required inputMode="decimal" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="49,00" />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={edit.creditOnRepair} onCheckedChange={(v) => setEdit({ ...edit, creditOnRepair: v })} /> Bei Durchführung der Reparatur verrechnen
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={edit.autoAdd} onCheckedChange={(v) => setEdit({ ...edit, autoAdd: v })} /> Bei der Geräteannahme vorauswählen
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Aktiv
                </label>
              </DialogBody>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEdit(null)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!edit?.name.trim() || parseEuro(edit?.price ?? '') === null}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function BillingPage() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<BillingStatus | 'rates' | 'customers'>(params.get('tab') === 'customers' && can('billing.manage') ? 'customers' : 'READY');
  const { data: counts } = useQuery({ queryKey: ['billing', 'counts'], queryFn: () => api<Record<BillingStatus, number>>('/billing/counts') });
  const { data: changes } = useQuery({ queryKey: ['customer-changes', 'OPEN'], queryFn: () => api<{ open: number }>('/customer-changes?status=OPEN'), enabled: can('billing.manage') });
  return (
    <div>
      <PageHeader title="Abrechnung" description="Vorgänge, die bereit zur Rechnung sind, zur Übernahme in Powerbird. Rechnungen und Kasse laufen in Powerbird." />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'READY', label: `Bereit zur Rechnung${counts ? ` (${counts.READY})` : ''}` },
          { value: 'TRANSFERRED', label: 'In Powerbird übernommen' },
          { value: 'NOT_BILLABLE', label: 'Keine Rechnung' },
          { value: 'rates', label: 'Pauschalen' },
          ...(can('billing.manage') ? [{ value: 'customers' as const, label: `Kundenstammdaten${changes?.open ? ` (${changes.open})` : ''}` }] : []),
        ]}
      />
      <div className="mt-4">{tab === 'rates' ? <FlatRatesTab /> : tab === 'customers' ? <CustomerChangesTab /> : <RecordsTab key={tab} status={tab} />}</div>
    </div>
  );
}
